import { minderjaehrigeKinderSchema } from '@/features/Sparten/shared/schema/kfz/versicherungsnehmerSchema/versicherungsnehmerSchema';
import type { KfzCompanyWkzMeta } from '@/features/Sparten/shared/types/kfz/types';
import type { Company } from '@/features/Companies/types';
import { useCompaniesStore } from '@/features/Companies/store';
import reduce from 'lodash/reduce';
import { useContractStore, useInputStore } from '@/features/Sparten';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
  type SimpleChoiceOptionGesellschaftenTs,
} from '@/features/ChoicesLists/Simple/types';
import { useSimpleChoicesStore } from '@/features/ChoicesLists/Simple/store';
import { useHsnTsn } from '@/features/HsnTsn/useHsnTsn';
import { useGlobalStore } from '@/features/Global/store';
import { TARIFF_SEARCH_REQUEST_IDENTIFIER } from '@/features/Tariffs/types';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import { isPkwSparte } from '@/features/Sparten/shared/kfz/isPkwSparte';

export const kfzQuotePreInputMergeHandler = (data: Record<string, unknown>) => {
  const quoteData = data as {
    versicherungsnehmer: {
      minderjaehrigeKinder: unknown;
    };
    fahrerkreis: {
      nutzerliste: unknown;
    };
  };
  // If we have an object, which only contains undefined key value pairs, its getting stripped to an empty array
  // New quotes (after this release) will have other defaults, but we need this snippet to fix broken offers on the fly
  if (Array.isArray(quoteData.versicherungsnehmer.minderjaehrigeKinder)) {
    quoteData.versicherungsnehmer.minderjaehrigeKinder =
      minderjaehrigeKinderSchema.getDefault();
  }

  return quoteData;
};

export const kfzSpecifiedCompanyFilter = (): Company[] => {
  const wkz = isPkwSparte(useGlobalStore().sparte)
    ? '112'
    : useInputStore().allgemein.wkz;

  const companies: Company<KfzCompanyWkzMeta>[] = useCompaniesStore().data;

  return reduce(
    companies,
    (acc, value: Company) => {
      const { meta } = value;
      if (meta === null || meta.wkzs === undefined) {
        return acc;
      }

      if (meta.wkzs.includes(wkz)) {
        acc.push(value);
      }

      return acc;
    },
    [] as Company<KfzCompanyWkzMeta>[],
  );
};

export const kfzInventoryContractTitle = () => {
  const carInformation = useHsnTsn().information.value;
  const modell = carInformation?.modell;
  const licensePlate = useContractStore().fahrzeugdaten.kennzeichen;

  if (modell === undefined) {
    return licensePlate;
  }

  return `${modell} ${licensePlate}`;
};

export const kfzAlternateTariffSearchRequestIdentifier = () => {
  if (
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.VERSICHERUNGSART_VERSICHERER_TARIF_WECHSEL,
      SIMPLE_CHOICE_META.VERSICHERUNGSART_TARIF_VERSICHERER_WECHSEL,
    ).includes(useInputStore().allgemein.versicherungsart)
  ) {
    const insuranceCompanyInformation = useSimpleChoicesStore().data[
      SIMPLE_CHOICE.GESELLSCHAFTEN_TS
    ].values.find(
      (company) =>
        company.value === useInputStore().vorversicherung.gesellschaftID,
    ) as SimpleChoiceOptionGesellschaftenTs | undefined;

    if (
      insuranceCompanyInformation &&
      insuranceCompanyInformation.insuranceId > 0
    ) {
      return TARIFF_SEARCH_REQUEST_IDENTIFIER.KFZ_AENDERUNGSANTRAG;
    }
  }

  return undefined;
};
